import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api_urls } from "../../utilities";
import { deleteService, getService, postService } from "../services";

function isSuccessCode(code) {
  return typeof code === "number" && code >= 200 && code < 300;
}

function isAgentRecord(row) {
  const raw = row?.user_type ?? row?.role ?? row?.type;
  if (raw == null || raw === "") return false;
  return String(raw).trim().toLowerCase() === "agent";
}

/**
 * `GET /api/admin/agents` — same envelope as other admin routes: { code, message, data: Agent[] }.
 * (Do not use `all-users` + client filter: that list can omit `user_type: "agent"` or differ, so the table stayed empty while Postman on `/agents` worked.)
 */
export const fetchAgents = createAsyncThunk(
  "agent/fetchAgents",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getService(api_urls.getAgents, {});
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      const raw = envelope.data;
      const list = Array.isArray(raw) ? raw : [];
      // Endpoint is agent-only; keep filter so mixed payloads still work.
      const filtered = list.filter(isAgentRecord);
      return filtered.length > 0 ? filtered : list;
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const createAgent = createAsyncThunk(
  "agent/createAgent",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postService(api_urls.createAgent, payload);
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && !isSuccessCode(code)) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return envelope.data ?? envelope;
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const deleteAgent = createAsyncThunk(
  "agent/deleteAgent",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteService(api_urls.deleteAgent(id));
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && !isSuccessCode(code)) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return { id, data: envelope.data ?? envelope };
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const fetchAgentById = createAsyncThunk(
  "agent/fetchAgentById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getService(api_urls.getAgent(id), {});
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && !isSuccessCode(code)) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return envelope.data ?? envelope;
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

const initialState = {
  list: [],
  single: null,
  loading: false,
  creating: false,
  deleting: false,
  singleLoading: false,
};

export const agentSlice = createSlice({
  name: "agent",
  initialState,
  reducers: {
    clearSingle: (state) => {
      state.single = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAgents.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchAgents.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createAgent.pending, (state) => {
        state.creating = true;
      })
      .addCase(createAgent.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createAgent.rejected, (state) => {
        state.creating = false;
      })
      .addCase(deleteAgent.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteAgent.fulfilled, (state, { payload }) => {
        state.deleting = false;
        if (payload?.id != null) {
          state.list = state.list.filter((a) => String(a.id) !== String(payload.id));
        }
      })
      .addCase(deleteAgent.rejected, (state) => {
        state.deleting = false;
      })
      .addCase(fetchAgentById.pending, (state) => {
        state.singleLoading = true;
      })
      .addCase(fetchAgentById.fulfilled, (state, { payload }) => {
        state.singleLoading = false;
        state.single = payload;
      })
      .addCase(fetchAgentById.rejected, (state) => {
        state.singleLoading = false;
      });
  },
});

export const { clearSingle } = agentSlice.actions;
export default agentSlice.reducer;
